import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, DEFAULT_LANGUAGE, getStoredLanguage, setStoredLanguage } from '../index';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  t: Record<string, any>; // Для больших объектов переводов
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = getStoredLanguage();
    return stored;
  });
  const [translations, setTranslations] = useState<Record<string, any>>({});

  // Expose global cache clearing function for cache reset page
  useEffect(() => {
    (window as any).clearTranslationCache = async () => {
      setTranslations({});
      // Trigger re-render
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
    };
    
    return () => {
      delete (window as any).clearTranslationCache;
    };
  }, [language]);

  useEffect(() => {
    const storedLanguage = getStoredLanguage();
    if (storedLanguage !== language) {
      setLanguageState(storedLanguage);
    }
  }, []);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    setStoredLanguage(newLanguage);
    
    // Trigger re-render of all T components
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: newLanguage }));
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ru' ? 'en' : 'ru');
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    toggleLanguage,
    t: translations
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};